import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBell, FaTimes, FaCheck, FaCheckDouble } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationBell = ({ panelPosition = 'right' }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Detectar si es admin o usuario normal
  const getToken = () => localStorage.getItem('deisycfit_token') || localStorage.getItem('deisycfit_admin_token');
  const isAdmin = () => {
    const adminToken = localStorage.getItem('deisycfit_admin_token');
    return !!adminToken;
  };

  const fetchNotifications = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = isAdmin() 
        ? `${API}/api/admin/notifications`
        : `${API}/api/user/notifications`;
      
      const response = await axios.get(endpoint, { headers });
      
      // Manejar diferentes formatos de respuesta
      if (isAdmin()) {
        setNotifications(response.data.notifications || []);
        setUnreadCount(response.data.unread_count || 0);
      } else {
        setNotifications(response.data || []);
        // Contar no leídas
        const unread = (response.data || []).filter(n => !n.read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = isAdmin()
        ? `${API}/api/admin/notifications/unread-count`
        : `${API}/api/user/notifications/unread-count`;
      
      const response = await axios.get(endpoint, { headers });
      setUnreadCount(response.data.unread_count || response.data.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  const handleOpen = async () => {
    setIsOpen(true);
    setLoading(true);
    await fetchNotifications();
    setLoading(false);
  };

  const handleMarkAsRead = async (notificationId) => {
    const token = getToken();
    if (!token) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = isAdmin()
        ? `${API}/api/admin/notifications/${notificationId}/read`
        : `${API}/api/user/notifications/${notificationId}/read`;
      
      await axios.put(endpoint, {}, { headers });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    const token = getToken();
    if (!token) return;

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const endpoint = isAdmin()
        ? `${API}/api/admin/notifications/mark-all-read`
        : `${API}/api/user/notifications/mark-all-read`;
      
      await axios.put(endpoint, {}, { headers });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (error) {
      toast.error('Error al marcar notificaciones');
    }
  };

  const handleNotificationClick = (notification) => {
    const isRead = notification.is_read ?? notification.read ?? false;
    if (!isRead) {
      handleMarkAsRead(notification.id);
    }
    
    // Navegar según el tipo de notificación (solo para usuarios)
    if (!isAdmin()) {
      if (notification.plan_type === 'diet') {
        navigate('/my-diet');
        setIsOpen(false);
      } else if (notification.plan_type === 'workout') {
        navigate('/my-workout');
        setIsOpen(false);
      } else if (notification.type === 'progress_comment') {
        // Navegar a la sección de progreso del usuario
        navigate('/my-progress');
        setIsOpen(false);
      }
    }
  };

  const getNotificationIcon = (type, planType) => {
    switch (type) {
      case 'payment': return '💰';
      case 'payment_failed': return '⚠️';
      case 'new_user': return '👋';
      case 'booking': return '📅';
      case 'form_submitted': return '📋';
      case 'plan_ready': return planType === 'diet' ? '🥗' : '💪';
      case 'progress_comment': return '💬';
      default: return '🔔';
    }
  };

  const getNotificationColor = (type, planType) => {
    switch (type) {
      case 'payment': return 'border-green-500 bg-green-500/10';
      case 'payment_failed': return 'border-yellow-500 bg-yellow-500/10';
      case 'new_user': return 'border-blue-500 bg-blue-500/10';
      case 'booking': return 'border-pink-500 bg-pink-500/10';
      case 'form_submitted': return 'border-purple-500 bg-purple-500/10';
      case 'progress_comment': return 'border-cyan-500 bg-cyan-500/10';
      case 'plan_ready': return planType === 'diet' 
        ? 'border-cyan-500 bg-cyan-500/10' 
        : 'border-pink-500 bg-pink-500/10';
      default: return 'border-gray-500 bg-gray-500/10';
    }
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={handleOpen}
        data-testid="notification-bell"
        className="relative p-2 text-gray-400 hover:text-white transition-colors rounded-full hover:bg-white/10"
      >
        <FaBell className="text-xl" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.span>
        )}
      </button>

      {/* Notification Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Panel — responsive: fixed en móvil (para que no se corte)
                y absolute anclado a la campana en desktop. */}
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              className={`fixed sm:absolute top-[64px] sm:top-12 inset-x-2 sm:inset-x-auto ${
                panelPosition === 'left' ? 'sm:left-0' : 'sm:right-0'
              } sm:w-96 max-h-[80vh] sm:max-h-[500px] overflow-hidden rounded-xl z-50`}
              style={{
                backgroundColor: '#1a1a2e',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
              }}
            >
              {/* Header */}
              <div className="flex justify-between items-center p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <FaBell className="text-pink-500" />
                  <h3 className="font-bold text-white">Notificaciones</h3>
                  {unreadCount > 0 && (
                    <span className="bg-pink-500 text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadCount} nuevas
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllRead}
                      className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                    >
                      <FaCheckDouble /> Marcar todas
                    </button>
                  )}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="text-gray-400 hover:text-white p-1"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* Notifications List */}
              <div className="overflow-y-auto max-h-[400px]">
                {loading ? (
                  <div className="p-8 text-center text-gray-400">
                    Cargando...
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <div className="text-4xl mb-2">🔔</div>
                    <p className="text-gray-400">No hay notificaciones</p>
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {notifications.map((notification) => {
                      // Handle both is_read and read fields for backward compatibility
                      const isRead = notification.is_read ?? notification.read ?? false;
                      return (
                        <motion.div
                          key={notification.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`p-4 hover:bg-white/5 transition-colors cursor-pointer ${
                            !isRead ? 'bg-white/5' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border ${getNotificationColor(notification.type, notification.plan_type)}`}>
                              {getNotificationIcon(notification.type, notification.plan_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex justify-between items-start gap-2">
                                <p className={`font-medium truncate ${isRead ? 'text-gray-400' : 'text-white'}`}>
                                  {notification.title}
                                </p>
                                {!isRead && (
                                  <span className="w-2 h-2 bg-pink-500 rounded-full flex-shrink-0 mt-2" />
                                )}
                              </div>
                              <p className="text-sm text-gray-500 truncate">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-600 mt-1">
                                {notification.created_at && formatDistanceToNow(new Date(notification.created_at), { 
                                  addSuffix: true,
                                  locale: es
                                })}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
